import {
  Badge,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  NumberInput,
  Popover,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SvgIconRaw from "~/components/SvgIconRaw";
import { multiSelectSurfaceStyles } from "~/utils/Theme";
import type { LooseObject } from "~/utils/Types";
import { isUsdaGradeBandId } from "~/utils/usdaGradeBands";

import {
  countActiveFilters,
  getFilterOptions,
  getUsdaGradeOptions,
  isBodyIdRangeInvalid,
  toDcuLiteFilter,
  toMsaFilter,
  usdaGradeLabelKey,
} from "./filtering";
import type {
  ImagesFilterAvailability,
  ImagesFilterState,
  ImagesModalRowKeys,
} from "./types";
import { EMPTY_FILTER_STATE } from "./types";

/** The control keys off whether a row carries an MSA marbling value, which is
 *  not the MSA/non-MSA graded split it reads as. Restore once the feed carries
 *  that distinction. */
const IS_MSA_FILTER_VISIBLE: boolean = false;

/**
 * Keeps each select's option list inside the popover's own dropdown rather
 * than a body portal. The popover dismisses on mousedown anywhere outside its
 * DOM, and a portaled option list counts as outside: it then unmounts once its
 * exit fade ends, so a press held longer than that fade selects nothing.
 */
export const POPOVER_COMBOBOX_PROPS = { withinPortal: false } as const;

const FiltersPopover = ({
  availability,
  filterControls,
  filters,
  keys,
  onFiltersChange,
  rows,
}: {
  availability: ImagesFilterAvailability;
  filterControls?: ReactNode;
  filters: ImagesFilterState;
  keys: ImagesModalRowKeys;
  onFiltersChange: (filters: ImagesFilterState) => void;
  /** Unfiltered rows, so the option lists do not shrink as filters narrow. */
  rows: LooseObject[];
}) => {
  const { t } = useTranslation("camera");
  const [isOpen, setIsOpen] = useState(false);

  const graderOptions = useMemo(
    () => (availability.hasGrader ? getFilterOptions(rows, keys.grader) : []),
    [availability.hasGrader, rows, keys.grader],
  );
  const sexOptions = useMemo(
    () => (availability.hasSex ? getFilterOptions(rows, keys.sex) : []),
    [availability.hasSex, rows, keys.sex],
  );
  // The grade filter is the region's: a US preset displays the USDA marbling
  // score and offers its grade bands; an AUS preset displays the AUS-MEAT
  // marble score and offers its values. IMF is never a filter.
  const usdaGradeOptions = useMemo(
    () =>
      availability.hasUsdaGrade
        ? getUsdaGradeOptions(rows, keys.usdaMarbling).map((id) => ({
            value: id,
            label: t(usdaGradeLabelKey(id)),
          }))
        : [],
    [availability.hasUsdaGrade, rows, keys.usdaMarbling, t],
  );
  const ausMarblingOptions = useMemo(
    () =>
      availability.hasAusMarbling
        ? getFilterOptions(rows, keys.ausMarbling)
        : [],
    [availability.hasAusMarbling, rows, keys.ausMarbling],
  );
  const meatColorOptions = useMemo(
    () =>
      availability.hasMeatColor ? getFilterOptions(rows, keys.meatColor) : [],
    [availability.hasMeatColor, rows, keys.meatColor],
  );
  const fatColorOptions = useMemo(
    () =>
      availability.hasFatColor ? getFilterOptions(rows, keys.fatColor) : [],
    [availability.hasFatColor, rows, keys.fatColor],
  );

  const activeCount = countActiveFilters(filters);
  const isRangeInvalid = isBodyIdRangeInvalid(filters);

  const update = (patch: Partial<ImagesFilterState>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <Popover
      opened={isOpen}
      onChange={setIsOpen}
      position="bottom-start"
      shadow="md"
      width={320}
    >
      <Popover.Target>
        <Button
          variant="secondary"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          leftSection={<SvgIconRaw name="Sliders" size={16} />}
          rightSection={
            activeCount > 0 ? (
              <Badge size="sm" circle>
                {activeCount}
              </Badge>
            ) : undefined
          }
        >
          {t("imagesModal.filters")}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={12}>
          {availability.hasGrader && (
            <MultiSelect
              label={t("imagesModal.filterGrader")}
              placeholder={
                filters.graders.length > 0
                  ? undefined
                  : t("imagesModal.filterAll")
              }
              data={graderOptions}
              value={filters.graders}
              onChange={(graders) => update({ graders })}
              // The theme hides MultiSelect pills for selects that summarise
              // their own selection; this popover has no such summary, so
              // rebuild the themed surface without the pill override.
              styles={multiSelectSurfaceStyles}
              comboboxProps={POPOVER_COMBOBOX_PROPS}
              searchable
              clearable
            />
          )}
          {availability.hasUsdaGrade && (
            <MultiSelect
              label={t("imagesModal.filterUsdaGrade")}
              placeholder={
                filters.usdaGrades.length > 0
                  ? undefined
                  : t("imagesModal.filterAll")
              }
              data={usdaGradeOptions}
              value={filters.usdaGrades}
              onChange={(values) =>
                update({ usdaGrades: values.filter(isUsdaGradeBandId) })
              }
              styles={multiSelectSurfaceStyles}
              comboboxProps={POPOVER_COMBOBOX_PROPS}
              clearable
            />
          )}
          {availability.hasAusMarbling && (
            <MultiSelect
              label={t("imagesModal.filterAusMarbling")}
              placeholder={
                filters.ausMarblings.length > 0
                  ? undefined
                  : t("imagesModal.filterAll")
              }
              data={ausMarblingOptions}
              value={filters.ausMarblings}
              onChange={(ausMarblings) => update({ ausMarblings })}
              styles={multiSelectSurfaceStyles}
              comboboxProps={POPOVER_COMBOBOX_PROPS}
              clearable
            />
          )}
          {availability.hasMeatColor && (
            <MultiSelect
              label={t("imagesModal.filterMeatColor")}
              placeholder={
                filters.meatColors.length > 0
                  ? undefined
                  : t("imagesModal.filterAll")
              }
              data={meatColorOptions}
              value={filters.meatColors}
              onChange={(meatColors) => update({ meatColors })}
              styles={multiSelectSurfaceStyles}
              comboboxProps={POPOVER_COMBOBOX_PROPS}
              clearable
            />
          )}
          {availability.hasFatColor && (
            <MultiSelect
              label={t("imagesModal.filterFatColor")}
              placeholder={
                filters.fatColors.length > 0
                  ? undefined
                  : t("imagesModal.filterAll")
              }
              data={fatColorOptions}
              value={filters.fatColors}
              onChange={(fatColors) => update({ fatColors })}
              styles={multiSelectSurfaceStyles}
              comboboxProps={POPOVER_COMBOBOX_PROPS}
              clearable
            />
          )}
          {availability.hasSex && (
            <MultiSelect
              label={t("imagesModal.filterSex")}
              placeholder={
                filters.sexes.length > 0
                  ? undefined
                  : t("imagesModal.filterAll")
              }
              data={sexOptions}
              value={filters.sexes}
              onChange={(sexes) => update({ sexes })}
              styles={multiSelectSurfaceStyles}
              comboboxProps={POPOVER_COMBOBOX_PROPS}
              clearable
            />
          )}
          {IS_MSA_FILTER_VISIBLE && availability.hasMsa && (
            <Stack gap={4}>
              <Text size="xs" fw={500}>
                {t("imagesModal.filterMsaGrading")}
              </Text>
              <SegmentedControl
                aria-label={t("imagesModal.filterMsaGrading")}
                fullWidth
                value={filters.msaGrading}
                onChange={(value) => update({ msaGrading: toMsaFilter(value) })}
                data={[
                  { label: t("imagesModal.filterAll"), value: "all" },
                  { label: t("imagesModal.filterMsaOnly"), value: "msa" },
                  { label: t("imagesModal.filterNonMsaOnly"), value: "nonMsa" },
                ]}
              />
            </Stack>
          )}
          {availability.hasDcuLite && (
            <Stack gap={4}>
              <Text size="xs" fw={500}>
                {t("imagesModal.filterDcuLite")}
              </Text>
              <SegmentedControl
                aria-label={t("imagesModal.filterDcuLite")}
                fullWidth
                value={filters.dcuLite}
                onChange={(value) =>
                  update({ dcuLite: toDcuLiteFilter(value) })
                }
                data={[
                  { label: t("imagesModal.filterAll"), value: "all" },
                  {
                    label: t("imagesModal.filterDcuLiteOnly"),
                    value: "dcuLite",
                  },
                  {
                    label: t("imagesModal.filterStandardOnly"),
                    value: "standard",
                  },
                ]}
              />
            </Stack>
          )}
          {availability.hasEma && (
            <Checkbox
              label={t("imagesModal.filterEmaMissedOnly")}
              checked={filters.isEmaMissedOnly}
              onChange={(event) =>
                update({ isEmaMissedOnly: event.currentTarget.checked })
              }
            />
          )}
          {availability.hasBodyId && (
            <Stack gap={4}>
              <Text size="xs" fw={500}>
                {t("imagesModal.filterBodyIdRange")}
              </Text>
              <Group gap="xs" align="center" wrap="nowrap">
                <NumberInput
                  placeholder={t("imagesModal.filterBodyIdFrom")}
                  aria-label={t("imagesModal.filterBodyIdFrom")}
                  value={filters.bodyIdFrom}
                  onChange={(value) =>
                    update({
                      bodyIdFrom: typeof value === "number" ? value : "",
                    })
                  }
                  min={0}
                  hideControls
                  error={isRangeInvalid}
                />
                <NumberInput
                  placeholder={t("imagesModal.filterBodyIdTo")}
                  aria-label={t("imagesModal.filterBodyIdTo")}
                  value={filters.bodyIdTo}
                  onChange={(value) =>
                    update({ bodyIdTo: typeof value === "number" ? value : "" })
                  }
                  min={0}
                  hideControls
                  error={isRangeInvalid}
                />
              </Group>
              {isRangeInvalid && (
                <Text size="xs" c="red">
                  {t("imagesModal.filterBodyIdInvalid")}
                </Text>
              )}
            </Stack>
          )}
          {filterControls}
          <Button
            variant="subtle"
            disabled={activeCount === 0}
            onClick={() => onFiltersChange(EMPTY_FILTER_STATE)}
          >
            {t("imagesModal.filtersClear")}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default FiltersPopover;
